import React, { useState } from "react";
import { Form, Button, Alert } from "react-bootstrap";
import { useUser } from "../contexts/UserContext";
import DreamModal from "./DreamModal";
import PinCreationModal from "./PinCreationModal";
import {
  FaSignOutAlt,
  FaKey,
  FaCrown,
  FaCog,
  FaUser,
  FaEye,
  FaEyeSlash,
} from "react-icons/fa";
import SubscriptionManager from "./SubscriptionManager";
import "./AccountSettings.css";

const AccountSettings = ({ show, onHide }) => {
  const { user, logOut, subscriptionStatus, verifyPIN, checkUserHasPIN } =
    useUser();
  const [showConfirmLogout, setShowConfirmLogout] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showPinModal, setShowPinModal] = useState(false);
  const [showPinCreationModal, setShowPinCreationModal] = useState(false);
  const [pin, setPin] = useState("");
  const [pinError, setPinError] = useState("");
  const [showPin, setShowPin] = useState(false);
  const [isVerifyingPin, setIsVerifyingPin] = useState(false);
  const [isCheckingPin, setIsCheckingPin] = useState(false);

  const handleLogout = () => {
    setShowConfirmLogout(true);
  };

  const confirmLogout = () => {
    logOut();
    setShowConfirmLogout(false);
    onHide();
  };

  const handleManageSubscription = async () => {
    try {
      setIsCheckingPin(true);
      console.log("Checking if user has PIN...");

      const hasPIN = await checkUserHasPIN();

      if (hasPIN) {
        console.log("User has PIN, showing verification modal");
        setShowPinModal(true);
      } else {
        console.log("User has no PIN, showing creation modal");
        setShowPinCreationModal(true);
      }

      // Reset PIN form state
      setPin("");
      setPinError("");
      setShowPin(false);
    } catch (error) {
      console.error("Error checking PIN status:", error);
      setPinError("Failed to check PIN status. Please try again.");
    } finally {
      setIsCheckingPin(false);
    }
  };

  const handlePinCreated = () => {
    console.log(
      "PIN created successfully, proceeding to subscription management"
    );
    setShowSubscriptionModal(true);
  };

  const handlePinSubmit = async (e) => {
    e.preventDefault();
    setPinError("");

    if (!pin.trim()) {
      setPinError("Please enter your PIN");
      return;
    }

    if (pin.length < 4) {
      setPinError("PIN must be at least 4 characters");
      return;
    }

    try {
      setIsVerifyingPin(true);
      console.log("Verifying PIN for user:", user.email);

      // Verify PIN against stored hash
      const result = await verifyPIN(pin);

      if (result.success) {
        console.log("PIN verified successfully");
        // PIN verified, close PIN modal and open subscription modal
        setShowPinModal(false);
        setShowSubscriptionModal(true);
        setPin("");
      } else {
        console.log("PIN verification failed:", result.error);
        setPinError(result.error || "Invalid PIN");
      }
    } catch (err) {
      console.error("Error verifying PIN:", err);
      setPinError("Failed to verify PIN. Please try again.");
    } finally {
      setIsVerifyingPin(false);
    }
  };

  const handlePinCancel = () => {
    setShowPinModal(false);
    setPin("");
    setPinError("");
    setShowPin(false);
  };

  return (
    <>
      <DreamModal
        show={show}
        onHide={onHide}
        title={
          <>
            <FaCog className="me-2" />
            Account Settings
          </>
        }
        size="md"
        className="account-settings-modal"
      >
        <div className="account-info-grid">
          {/* Combined Account & Subscription Card */}
          <div className="info-card account-card">
            <div className="info-card-header">
              <FaUser className="info-icon" />
              <h6>Account Information</h6>
            </div>
            <div className="account-details">
              <div className="account-email-section">
                <label className="account-label">Logged in as:</label>
                <div className="account-email">{user?.email}</div>
              </div>

              <div className="subscription-section-inline">
                <div className="subscription-info-inline">
                  <label className="account-label">Subscription:</label>
                  <div className="subscription-status">
                    {subscriptionStatus?.status === "active" ? (
                      <div className="premium-status-badge">
                        <FaCrown className="crown-icon-badge" />
                        <span>Premium Member</span>
                      </div>
                    ) : (
                      <div className="free-status">
                        <span>Free Plan</span>
                      </div>
                    )}
                  </div>
                </div>
                <Button
                  variant="primary"
                  className="manage-subscription-btn"
                  onClick={handleManageSubscription}
                  disabled={isCheckingPin}
                >
                  {isCheckingPin ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Loading...
                    </>
                  ) : (
                    <>
                      <FaCrown className="me-2" />
                      {subscriptionStatus?.status === "active"
                        ? "Manage"
                        : "Upgrade"}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>

          {/* Logout Card */}
          <div className="info-card logout-card">
            <div className="info-card-header">
              <FaSignOutAlt className="info-icon" />
              <h6>Sign Out</h6>
            </div>
            <div className="logout-content">
              <p className="logout-description">
                Your progress will be saved automatically
              </p>
              <Button
                variant="danger"
                onClick={handleLogout}
                className="logout-btn"
              >
                <FaSignOutAlt className="me-2" />
                Log Out
              </Button>
            </div>
          </div>
        </div>

        {/* Logout Confirmation */}
        {showConfirmLogout && (
          <div className="logout-confirmation">
            <Alert variant="warning">
              <Alert.Heading>Confirm Logout</Alert.Heading>
              <p>
                Are you sure you want to log out? Your progress will be saved.
              </p>
              <div className="confirmation-buttons">
                <Button
                  variant="secondary"
                  onClick={() => setShowConfirmLogout(false)}
                  className="cancel-button"
                >
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  onClick={confirmLogout}
                  className="confirm-button"
                >
                  Log Out
                </Button>
              </div>
            </Alert>
          </div>
        )}
      </DreamModal>

      {/* PIN Creation Modal */}
      <PinCreationModal
        show={showPinCreationModal}
        onHide={() => setShowPinCreationModal(false)}
        onPinCreated={handlePinCreated}
        title="Create Account PIN"
      />

      {/* PIN Verification Modal */}
      <DreamModal
        show={showPinModal}
        onHide={handlePinCancel}
        title={
          <>
            <FaKey className="me-2" />
            Verify Your PIN
          </>
        }
        size="sm"
        className="pin-verification-modal"
      >
        <div className="pin-verification-content">
          <div className="text-center mb-4">
            <div className="pin-icon-wrapper">
              <FaKey className="pin-icon" />
            </div>
            <h5>Security Verification</h5>
            <p className="text-muted">
              Please enter your account PIN to manage your subscription
            </p>
          </div>

          <Form onSubmit={handlePinSubmit}>
            {pinError && (
              <Alert variant="danger" className="mb-3">
                {pinError}
              </Alert>
            )}

            <Form.Group className="mb-4">
              <Form.Label>Account PIN</Form.Label>
              <div className="pin-input-wrapper">
                <Form.Control
                  type={showPin ? "text" : "password"}
                  placeholder="Enter your PIN"
                  value={pin}
                  onChange={(e) => setPin(e.target.value)}
                  className="pin-input"
                  autoFocus
                />
                <Button
                  variant="outline-secondary"
                  className="pin-toggle-btn"
                  onClick={() => setShowPin(!showPin)}
                  type="button"
                >
                  {showPin ? <FaEyeSlash /> : <FaEye />}
                </Button>
              </div>
            </Form.Group>

            <div className="pin-actions">
              <Button
                variant="secondary"
                onClick={handlePinCancel}
                className="cancel-pin-btn"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                className="verify-pin-btn"
                disabled={isVerifyingPin}
              >
                {isVerifyingPin ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Verifying...
                  </>
                ) : (
                  "Verify PIN"
                )}
              </Button>
            </div>
          </Form>
        </div>
      </DreamModal>

      {/* Subscription Modal */}
      <DreamModal
        show={showSubscriptionModal}
        onHide={() => setShowSubscriptionModal(false)}
        title={
          <>
            <FaCrown className="me-2" />
            {subscriptionStatus?.active
              ? "Manage Subscription"
              : "Upgrade to Premium"}
          </>
        }
        className="subscription-modal"
        size="md"
      >
        <SubscriptionManager />
      </DreamModal>
    </>
  );
};

export default AccountSettings;
